import React, { useCallback, useEffect, useState } from "react";
import { Row, Col } from "antd";
import { useIntl } from "react-intl";

import { formItemId } from "../product";
import {
  getVolume,
  isLdmItem,
  getLoadingMeters,
  getCalculatedWeight,
} from "../../../../modules/consignmentFunctions";
import SelectItem from "../../../app/generic/selectItem";
import CheckboxItem from "../../../app/generic/checkboxItem";
import TableRowActions from "../../../app/generic/tableRowActions";
import InputNumberItem from "../../../app/generic/inputNumberItem";
import SelectItemWithLoad from "../../../app/generic/selectItemWithLoad";

const propStackable = "stackable",
  propCount = "count",
  propLength = "length",
  propHeight = "height",
  propWidth = "width";

const ColliRow = ({
  adr,
  form,
  index,
  rules,
  products,
  wrappings,
  onColliDelete,
  disabledDelete,
  formItemLayout,
  onSetStackables,
  onAnyValueChange,
  getFieldDecorator,
  selectedProductId,
  forceLoadingMeters,
  shouldNotBeStackable,
}) => {
  const intl = useIntl();
  const handleColliDelete = useCallback(() => {
    onColliDelete(index);
    onAnyValueChange();
  }, [index, onAnyValueChange, onColliDelete]);

  const [visibleLoadingMeters, setVisibleLoadingMeters] = useState();
  const handleLoadingMetersSet = useCallback(
    (value, prop, currentAdr) => {
      onAnyValueChange();
      let show = false,
        showByValue = false;
      const productId = form.getFieldValue(formItemId);
      if (productId) {
        const product = products.find((p) => p.id === productId);
        if (product && !product.domestic) {
          const count =
              prop === propCount
                ? value
                : form.getFieldValue(`${propCount}s[${index}]`),
            height =
              prop === propHeight
                ? value
                : form.getFieldValue(`${propHeight}s[${index}]`),
            length =
              prop === propLength
                ? value
                : form.getFieldValue(`${propLength}s[${index}]`),
            width =
              prop === propWidth
                ? value
                : form.getFieldValue(`${propWidth}s[${index}]`),
            stackable =
              prop === propStackable
                ? value
                : form.getFieldValue(`${propStackable}s[${index}]`),
            adr =
              currentAdr !== undefined ? currentAdr : form.getFieldValue("adr"),
            loadingMeters = getLoadingMeters(
              { stackable, height, length, width },
              adr,
              count
            );
          showByValue = isLdmItem({ stackable, height }, adr);
          show = forceLoadingMeters || showByValue;
          setVisibleLoadingMeters(show);
          if (show && !isNaN(loadingMeters)) {
            const val = {};
            val[`loadingMeters[${index}]`] = loadingMeters;
            setTimeout(() => form.setFieldsValue(val), 500);
          }
        }
      }
      setVisibleLoadingMeters(show);
      onSetStackables((old) => {
        const n = { ...old };
        n[index] = showByValue;
        return n;
      });
    },
    [
      form,
      index,
      products,
      onSetStackables,
      onAnyValueChange,
      forceLoadingMeters,
    ]
  );

  const handleVolumeChange = useCallback(
    (value, prop) => {
      const count =
          prop === propCount
            ? value
            : form.getFieldValue(`${propCount}s[${index}]`),
        height =
          prop === propHeight
            ? value
            : form.getFieldValue(`${propHeight}s[${index}]`),
        length =
          prop === propLength
            ? value
            : form.getFieldValue(`${propLength}s[${index}]`),
        width =
          prop === propWidth
            ? value
            : form.getFieldValue(`${propWidth}s[${index}]`),
        volume = getVolume(length, width, height, count),
        calculatedWeight = getCalculatedWeight({ height, length, width }, adr);
      const val = {};
      val[`volumes[${index}]`] = isNaN(volume) ? 0 : volume;
      if (rules?.colliCalculatedWeight) {
        val[`calculatedWeights[${index}]`] = isNaN(calculatedWeight)
          ? 0
          : calculatedWeight;
      }
      form.setFieldsValue(val);
    },
    [adr, form, index, rules]
  );

  const handleCountChange = useCallback(
    (value) => {
      handleLoadingMetersSet(value, propCount);
      handleVolumeChange(value, propCount);
    },
    [handleVolumeChange, handleLoadingMetersSet]
  );
  const handleLengthChange = useCallback(
    (value) => {
      handleLoadingMetersSet(value, propLength);
      handleVolumeChange(value, propLength);
    },
    [handleVolumeChange, handleLoadingMetersSet]
  );
  const handleHeightChange = useCallback(
    (value) => {
      handleLoadingMetersSet(value, propHeight);
      handleVolumeChange(value, propHeight);
    },
    [handleVolumeChange, handleLoadingMetersSet]
  );
  const handleWidthChange = useCallback(
    (value) => {
      handleLoadingMetersSet(value, propWidth);
      handleVolumeChange(value, propWidth);
    },
    [handleVolumeChange, handleLoadingMetersSet]
  );
  const handleStackableChange = useCallback(
    (e) => {
      handleLoadingMetersSet(e.target.checked, propStackable);
    },
    [handleLoadingMetersSet]
  );

  const [currentProductId, setCurrentProductId] = useState();
  useEffect(() => {
    if (selectedProductId !== currentProductId) {
      setCurrentProductId(selectedProductId);
      handleLoadingMetersSet();
    }
  }, [currentProductId, selectedProductId, handleLoadingMetersSet]);

  const [oldAdr, setOldAdr] = useState(false);
  useEffect(() => {
    if (oldAdr !== adr) {
      setOldAdr(adr);
      handleLoadingMetersSet(undefined, undefined, adr);
    }
  }, [adr, oldAdr, handleLoadingMetersSet]);

  const [force, setForce] = useState();
  useEffect(() => {
    if (forceLoadingMeters !== force) {
      setForce(forceLoadingMeters);
      handleLoadingMetersSet();
    }
  }, [force, forceLoadingMeters, handleLoadingMetersSet]);

  const [oldShouldNotBeStackable, setOldShouldNotBeStackable] = useState(false);
  useEffect(() => {
    if (oldShouldNotBeStackable !== shouldNotBeStackable) {
      setOldShouldNotBeStackable(shouldNotBeStackable);
      const val = {},
        stackability = !form.getFieldValue("adr");
      val[`${propStackable}s[${index}]`] = stackability;
      form.setFieldsValue(val);
      handleLoadingMetersSet(stackability, propStackable, !stackability);
    }
  }, [
    form,
    index,
    shouldNotBeStackable,
    handleLoadingMetersSet,
    oldShouldNotBeStackable,
  ]);

  return (
    <Row gutter={20} type="flex">
      <Col span={23}>
        <Row type="flex">
          <Col span={24}>
            <Row>
              <Col span={4} className="center">
                <InputNumberItem
                  fieldName={`${propCount}s[${index}]`}
                  label={intl.formatMessage({
                    id: "consignment.colli.count",
                    defaultMessage: "Počet kusů",
                  })}
                  initialValue={1}
                  max={rules && rules.count}
                  formItemLayout={formItemLayout}
                  onInputChange={handleCountChange}
                  getFieldDecorator={getFieldDecorator}
                  message={intl.formatMessage({
                    id: "consignment.colli.emptyCount",
                    defaultMessage: "Zadejte prosím počet kusů!",
                  })}
                />
              </Col>
              <Col span={6} className="center">
                <SelectItemWithLoad skeleton options={wrappings}>
                  <SelectItem
                    allowClear
                    wrapperCol={22}
                    initialValue={9}
                    options={wrappings}
                    onSelect={onAnyValueChange}
                    formItemLayout={formItemLayout}
                    fieldName={`wrappings[${index}]`}
                    getFieldDecorator={getFieldDecorator}
                    label={intl.formatMessage({
                      id: "consignment.colli.wrapping",
                      defaultMessage: "Druh obalu",
                    })}
                    message={intl.formatMessage({
                      id: "consignment.colli.emptyWrapping",
                      defaultMessage: "Vyberte prosím druh obalu!",
                    })}
                  />
                </SelectItemWithLoad>
              </Col>
              <Col span={3} className="center">
                <InputNumberItem
                  unit="cm"
                  label={intl.formatMessage({
                    id: "calculator.length",
                    defaultMessage: "Délka",
                  })}
                  formItemLayout={formItemLayout}
                  message={intl.formatMessage({
                    id: "consignment.colli.emptyLength",
                    defaultMessage: "Zadejte prosím délku!",
                  })}
                  min={rules?.colliMinLength ?? 1}
                  max={rules && rules.colliLength}
                  onInputChange={handleLengthChange}
                  getFieldDecorator={getFieldDecorator}
                  fieldName={`${propLength}s[${index}]`}
                />
              </Col>
              <Col span={3} className="center">
                <InputNumberItem
                  unit="cm"
                  label={intl.formatMessage({
                    id: "calculator.width",
                    defaultMessage: "Šířka",
                  })}
                  formItemLayout={formItemLayout}
                  message={intl.formatMessage({
                    id: "consignment.colli.emptyWidth",
                    defaultMessage: "Zadejte prosím šířku!",
                  })}
                  min={rules?.colliMinWidth ?? 1}
                  max={rules && rules.colliWidth}
                  onInputChange={handleWidthChange}
                  getFieldDecorator={getFieldDecorator}
                  fieldName={`${propWidth}s[${index}]`}
                />
              </Col>
              <Col span={3} className="center">
                <InputNumberItem
                  unit="cm"
                  label={intl.formatMessage({
                    id: "calculator.height",
                    defaultMessage: "Výška",
                  })}
                  formItemLayout={formItemLayout}
                  message={intl.formatMessage({
                    id: "consignment.colli.emptyHeight",
                    defaultMessage: "Zadejte prosím výšku!",
                  })}
                  min={rules?.colliMinHeight ?? 1}
                  max={rules && rules.colliHeight}
                  onInputChange={handleHeightChange}
                  getFieldDecorator={getFieldDecorator}
                  fieldName={`${propHeight}s[${index}]`}
                />
              </Col>
              <Col span={4} className="center">
                <InputNumberItem
                  unit="kg"
                  fieldName={`weights[${index}]`}
                  label={intl.formatMessage({
                    id: "calculator.weight",
                    defaultMessage: "Hmotnost",
                  })}
                  formItemLayout={formItemLayout}
                  message={intl.formatMessage({
                    id: "consignment.colli.emptyWeight",
                    defaultMessage: "Zadejte prosím hmotnost!",
                  })}
                  min={rules?.colliMinWeight ?? 1}
                  max={rules && rules.colliWeight}
                  onInputChange={onAnyValueChange}
                  getFieldDecorator={getFieldDecorator}
                />
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row>
              <Col span={4} className="center">
                <CheckboxItem
                  initialValue={true}
                  label={intl.formatMessage({
                    id: "consignment.colli.stackable",
                    defaultMessage: "Stohovatelné",
                  })}
                  formItemLayout={formItemLayout}
                  onChange={handleStackableChange}
                  getFieldDecorator={getFieldDecorator}
                  fieldName={`${propStackable}s[${index}]`}
                />
              </Col>
              {visibleLoadingMeters && (
                <Col span={4} className="center">
                  <InputNumberItem
                    disabled
                    min={0.001}
                    precision={3}
                    label={intl.formatMessage({
                      id: "consignment.colli.loadingMeters",
                      defaultMessage: "Ložné metry (LDM)",
                    })}
                    formItemLayout={formItemLayout}
                    onInputChange={onAnyValueChange}
                    getFieldDecorator={getFieldDecorator}
                    fieldName={`loadingMeters[${index}]`}
                    max={
                      rules?.colliLoadingMeters ?? rules?.loadingMeters ?? 99
                    }
                  />
                </Col>
              )}
              <Col span={4} className="center">
                <InputNumberItem
                  disabled
                  min={0.001}
                  precision={3}
                  max={rules?.volume ?? 99}
                  label={intl.formatMessage({
                    id: "consignment.colli.volume",
                    defaultMessage: "Objem [m³]",
                  })}
                  formItemLayout={formItemLayout}
                  fieldName={`volumes[${index}]`}
                  getFieldDecorator={getFieldDecorator}
                />
              </Col>
              {rules?.colliCalculatedWeight && (
                <Col span={4} className="center">
                  <InputNumberItem
                    disabled
                    min={0.001}
                    precision={0}
                    label={intl.formatMessage({
                      id: "consignment.colli.calculatedWeight",
                      defaultMessage: "Přepočtená váha [kg]",
                    })}
                    formItemLayout={formItemLayout}
                    getFieldDecorator={getFieldDecorator}
                    fieldName={`calculatedWeights[${index}]`}
                    max={rules?.colliCalculatedWeight ?? 9999}
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      </Col>
      <Col span={1} className="center calculation-form-colli-delete-container">
        <TableRowActions
          onDelete={handleColliDelete}
          disabledDelete={disabledDelete}
        />
      </Col>
    </Row>
  );
};

export default ColliRow;
